import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { setErrorLanguage } from '../utils/errorMessages';

// Load resources (bundled JSON) – keep small to start; can split per-namespace
import common_en from '../locales/en/common.json';
import common_zh from '../locales/zh/common.json';
import invitation_en from '../locales/en/invitation.json';
import invitation_zh from '../locales/zh/invitation.json';
import choice_en from '../locales/en/choice.json';
import choice_zh from '../locales/zh/choice.json';
import workflow_en from '../locales/en/workflow.json';
import workflow_zh from '../locales/zh/workflow.json';
import workflowProps_en from '../locales/en/workflowProps.json';
import workflowProps_zh from '../locales/zh/workflowProps.json';
import cloud_en from '../locales/en/cloud.json';
import cloud_zh from '../locales/zh/cloud.json';
import prompts_en from '../locales/en/prompts.json';
import prompts_zh from '../locales/zh/prompts.json';
import workflowCanvas_en from '../locales/en/workflowCanvas.json';
import workflowCanvas_zh from '../locales/zh/workflowCanvas.json';
import workflowExec_en from '../locales/en/workflowExec.json';
import workflowExec_zh from '../locales/zh/workflowExec.json';
import simplified_en from '../locales/en/simplifiedWorkflow.json';
import simplified_zh from '../locales/zh/simplifiedWorkflow.json';
import auth_en from '../locales/en/auth.json';
import auth_zh from '../locales/zh/auth.json';
import errors_en from '../locales/en/errors.json';
import errors_zh from '../locales/zh/errors.json';
import home_en from '../locales/en/home.json';
import home_zh from '../locales/zh/home.json';
import userProfile_en from '../locales/en/userProfile.json';
import userProfile_zh from '../locales/zh/userProfile.json';
import spreadsheet_en from '../locales/en/spreadsheet.json';
import spreadsheet_zh from '../locales/zh/spreadsheet.json';
import taskManager_en from '../locales/en/taskManager.json';
import taskManager_zh from '../locales/zh/taskManager.json';
import version_en from '../locales/en/version.json';
import version_zh from '../locales/zh/version.json';

// Simple, dependency-free i18n provider
// Usage: const { t, lang, changeLanguage } = useI18n(); t('namespace.key', { var: 'x' })

const I18nContext = createContext({ t: (k) => k, get: (k) => null, lang: 'en', changeLanguage: () => {} });

const RESOURCES = {
  en: {
    common: common_en,
    home: home_en,
    invitation: invitation_en,
    choice: choice_en,
    workflow: workflow_en,
    workflowProps: workflowProps_en,
    cloud: cloud_en,
    prompts: prompts_en,
    workflowCanvas: workflowCanvas_en,
    workflowExec: workflowExec_en,
    simplified: simplified_en,
    auth: auth_en,
    errors: errors_en,
    userProfile: userProfile_en,
    spreadsheet: spreadsheet_en,
    taskManager: taskManager_en,
    version: version_en
  },
  zh: {
    common: common_zh,
    home: home_zh,
    invitation: invitation_zh,
    choice: choice_zh,
    workflow: workflow_zh,
    workflowProps: workflowProps_zh,
    cloud: cloud_zh,
    prompts: prompts_zh,
    workflowCanvas: workflowCanvas_zh,
    workflowExec: workflowExec_zh,
    simplified: simplified_zh,
    auth: auth_zh,
    errors: errors_zh,
    userProfile: userProfile_zh,
    spreadsheet: spreadsheet_zh,
    taskManager: taskManager_zh,
    version: version_zh
  }
};

function escapeRegExp(string) {
  // Escape special regex characters
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function interpolate(str, vars) {
  if (!vars) return str;
  return Object.keys(vars).reduce((acc, key) => {
    const escapedKey = escapeRegExp(key);
    return acc.replace(new RegExp(`\\{${escapedKey}\\}`, 'g'), String(vars[key]));
  }, str);
}

export function I18nProvider({ children, defaultLang = 'en' }) {
  const [lang, setLang] = useState(() => localStorage.getItem('i18n_lang') || defaultLang);

  useEffect(() => {
    // Persist and sync language across app concerns
    localStorage.setItem('i18n_lang', lang);
    localStorage.setItem('preferredLanguage', lang); // keep legacy key in sync
    setErrorLanguage(lang); // sync errorMessages language
  }, [lang]);

  const t = useMemo(() => {
    return (key, vars) => {
      if (!key) return '';
      const [ns, ...rest] = key.split(':').length > 1 ? key.split(':') : ['common', key];
      const path = rest.join(':');
      const segs = path.split('.');
      let cursor = RESOURCES[lang]?.[ns];
      for (const s of segs) {
        if (cursor && Object.prototype.hasOwnProperty.call(cursor, s)) {
          cursor = cursor[s];
        } else {
          cursor = null;
          break;
        }
      }
      const val = typeof cursor === 'string' ? cursor : key; // fallback to key
      return interpolate(val, vars);
    };
  }, [lang]);

  const get = useMemo(() => {
    return (key) => {
      if (!key) return null;
      const [ns, ...rest] = key.split(':').length > 1 ? key.split(':') : ['common', key];
      const path = rest.join(':');
      const segs = path ? path.split('.') : [];
      let cursor = RESOURCES[lang]?.[ns];
      for (const s of segs) {
        if (cursor && Object.prototype.hasOwnProperty.call(cursor, s)) {
          cursor = cursor[s];
        } else {
          cursor = null;
          break;
        }
      }
      return cursor ?? null; // may be object/array/string/null
    };
  }, [lang]);

  const changeLanguage = (code) => setLang(code);

  const value = useMemo(() => ({ t, get, lang, changeLanguage }), [t, get, lang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
